import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Select, DatePicker, Button,
  Table, notification, Spin, Statistic, Tag, Space, Tooltip
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import { useHistory } from "react-router-dom";
import {
  FileTextOutlined, CalendarOutlined,
  CheckCircleOutlined, PieChartOutlined, ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

const fmt = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

function ExpenditureReports() {
  const history = useHistory();
  const now = new Date();

  // Last FY as default — most likely to have real data
  const lastFYStartYear = now.getMonth() >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
  const defaultFY = `${lastFYStartYear}-${String(lastFYStartYear + 1).slice(-2)}`;
  const fyStart = moment(`${lastFYStartYear}-04-01`);
  const fyEnd   = fyStart.clone().add(1, 'year').subtract(1, 'day');

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([fyStart, fyEnd]);
  const [fiscalYear, setFiscalYear] = useState(defaultFY);
  const [reportData, setReportData] = useState({ summary: {}, categoryWise: [], topBeneficiaries: [], monthlyTrend: [] });

  const categoryColumns = [
    { title: 'Category', dataIndex: 'category', key: 'category',
      render: (v) => <span style={{ fontWeight: 600, fontSize: 13 }}>{v?.replace('_', ' ')}</span> },
    { title: 'Count', dataIndex: 'total_expenditures', key: 'total_expenditures', align: 'center',
      render: (v) => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: 'Total', dataIndex: 'total_amount', key: 'total_amount',
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 12 }}>{fmt(v)}</span> },
    { title: 'Average', dataIndex: 'avg_amount', key: 'avg_amount',
      render: (v) => <span style={{ fontSize: 12 }}>{fmt(v)}</span> },
  ];

  const beneficiaryColumns = [
    { title: 'Beneficiary', dataIndex: 'beneficiary_name', key: 'beneficiary_name',
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
          <Tag style={{ fontSize: 10, background: '#eef2f8', color: '#7a0000', border: 'none' }}>{record.beneficiary_type}</Tag>
        </div>
      ) },
    { title: 'Count', dataIndex: 'total_expenditures', key: 'total_expenditures', align: 'center',
      render: (v) => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: 'Total', dataIndex: 'total_amount', key: 'total_amount',
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 12 }}>{fmt(v)}</span> },
  ];

  const goToMonth = (monthStr) => {
    const start = moment(monthStr).startOf('month').format('YYYY-MM-DD');
    const end   = moment(monthStr).endOf('month').format('YYYY-MM-DD');
    // Pass date range via sessionStorage so List page can read it
    sessionStorage.setItem('exp_quick_start', start);
    sessionStorage.setItem('exp_quick_end',   end);
    sessionStorage.setItem('exp_quick_label', moment(monthStr).format('MMMM YYYY'));
    history.push('/expenditures');
  };

  const monthlyColumns = [
    { title: 'Month', dataIndex: 'month', key: 'month',
      render: (v) => <span style={{ fontSize: 12, fontWeight: 600 }}>{moment(v).format('MMM YYYY')}</span> },
    { title: 'Count', dataIndex: 'total_expenditures', key: 'total_expenditures', align: 'center',
      render: (v) => <Tag color="blue" style={{ fontSize: 11 }}>{v} entries</Tag> },
    { title: 'Total Amount', dataIndex: 'total_amount', key: 'total_amount',
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 13 }}>{fmt(v)}</span> },
    { title: 'Average', dataIndex: 'avg_amount', key: 'avg_amount',
      render: (v) => <span style={{ fontSize: 12 }}>{fmt(v)}</span> },
    { title: 'View', key: 'action', align: 'center', width: 80,
      render: (_, record) => (
        <Tooltip title={`View all expenditures for ${moment(record.month).format('MMM YYYY')}`}>
          <Button size="small" icon={<EyeOutlined />} type="link"
            onClick={() => goToMonth(record.month)}
            style={{ color: '#1890ff', fontWeight: 600 }}>
            Records
          </Button>
        </Tooltip>
      )
    },
  ];

  useEffect(() => { fetchReports(); }, [dateRange, fiscalYear]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const [summaryRes, categoryRes, beneficiaryRes, monthlyRes] = await Promise.all([
        axios.get(`${baseUrl}/expenditure-reports/summary?start_date=${startDate}&end_date=${endDate}&fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }),
        axios.get(`${baseUrl}/expenditure-reports/category-wise?start_date=${startDate}&end_date=${endDate}`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }),
        axios.get(`${baseUrl}/expenditure-reports/top-beneficiaries?start_date=${startDate}&end_date=${endDate}&limit=10`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }),
        axios.get(`${baseUrl}/expenditure-reports/monthly-trend?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }),
      ]);
      setReportData({
        summary: summaryRes.data.result || {},
        categoryWise: categoryRes.data.result || [],
        topBeneficiaries: beneficiaryRes.data.result || [],
        monthlyTrend: monthlyRes.data.result || [],
      });
    } catch {
      notification.error({ message: 'Error', description: 'Failed to fetch expenditure reports' });
    } finally {
      setLoading(false);
    }
  };

  const getFiscalYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => {
      const y = currentYear - 2 + i;
      const fy = `${y}-${(y + 1).toString().slice(-2)}`;
      return <Option key={fy} value={fy}>FY {fy}</Option>;
    });
  };

  const s = reportData.summary;

  const statCards = [
    { title: 'Total Expenditures', value: s.total_expenditures || 0, display: s.total_expenditures || 0, accent: '#7a0000', bg: '#eef2f8', icon: <FileTextOutlined /> },
    { title: 'Total Amount',       value: s.total_amount || 0,       display: fmt(s.total_amount),       accent: '#0891b2', bg: '#e0f2fe', icon: <PieChartOutlined /> },
    { title: 'Approved Amount',    value: s.approved_amount || 0,    display: fmt(s.approved_amount),    accent: '#15803d', bg: '#dcfce7', icon: <CheckCircleOutlined /> },
    { title: 'Pending Amount',     value: s.pending_amount || 0,     display: fmt(s.pending_amount),     accent: '#d97706', bg: '#fef3c7', icon: <CalendarOutlined /> },
  ];

  return (
    <Spin spinning={loading}>
    <div style={{ padding: '20px 24px' }}>

      {/* Filter bar */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Period:</span>
        <RangePicker value={dateRange} onChange={v => v && setDateRange(v)} format="DD-MM-YYYY" style={{ borderRadius: 8 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>FY:</span>
        <Select value={fiscalYear} onChange={setFiscalYear} style={{ width: 130, borderRadius: 8 }}>
          {getFiscalYearOptions()}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={fetchReports}
          style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: 8, fontWeight: 600 }}>
          Generate
        </Button>
      </div>

      {/* Summary stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((sc, i) => (
          <Col key={i} xs={12} sm={6}>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', borderTop: `3px solid ${sc.accent}`, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{sc.title}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: sc.accent }}>{sc.display}</div>
                </div>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: sc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: sc.accent }}>
                  {sc.icon}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Detail tables */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 14, color: '#7a0000' }}><PieChartOutlined style={{ marginRight: 8, color: '#7a0000' }} />Category-wise Breakdown</span>}
            size="small" style={{ borderRadius: 12 }}>
            <Table columns={categoryColumns} size="small"
              dataSource={reportData.categoryWise.map((item, i) => ({ key: i, ...item }))}
              pagination={false} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 14, color: '#7a0000' }}>Top Beneficiaries</span>}
            size="small" style={{ borderRadius: 12 }}>
            <Table columns={beneficiaryColumns} size="small"
              dataSource={reportData.topBeneficiaries.map((item, i) => ({ key: i, ...item }))}
              pagination={false} />
          </Card>
        </Col>
        <Col span={24}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 14, color: '#7a0000' }}>Monthly Trend</span>}
            size="small" style={{ borderRadius: 12 }}>
            <Table columns={monthlyColumns} size="small"
              dataSource={reportData.monthlyTrend.map((item, i) => ({ key: i, ...item }))}
              pagination={false} scroll={{ x: 'max-content' }} />
          </Card>
        </Col>
      </Row>
    </div>
    </Spin>
  );
}

export default ExpenditureReports;
